/**
 * See: https://developers.google.com/web/fundamentals/instant-and-offline/web-storage/cache-api
 * The caches api allows a browser to cache a network response and match it against another request later.
 * It is complementary to the fetch api and can be also be used by service workers to offer a total offline experience.
 */

import stableStringify from 'json-stable-stringify';

export const CACHE_NAME = `fetch_cache_v1`;
export const DEFAULT_TTL = 2 * 24 * 60 * 60; // cache for 2 days (yestday + today)

function _getCacheRequest(url: string, options: RequestInit): Request {
  let cacheRequest = new Request(url, options);
  if (cacheRequest.method === `POST` && new URL(cacheRequest.url).pathname.startsWith(`/api/2.0/`)) {
    // queryapi makes JSON GET requests as POST e.g insights
    // caches api cannot cache POST requests, so we cache them as GET
    const jsonBody = stableStringify(JSON.parse(options.body.toString()));
    cacheRequest = new Request(`${url}:${jsonBody}`, {...options, method: `GET`, body: null});
  }
  return cacheRequest;
}

/**
 * @param response response from cache
 * @param ttl time-to-live threshold in seconds for response to be marked as stale
 * @return true if response above ttl limit
 */
function _isResponseStale(response: Response, ttl: number): boolean {
  const dateHeader = response.headers.get(`date`);
  const aliveMs = dateHeader ? Date.now() - new Date(dateHeader).getTime() : DEFAULT_TTL;
  return aliveMs > ttl * 1000;
}

/** Whether cache storage api is supported by browser */
export function canUseFetchCache(): boolean {
  return `caches` in window;
}

/**
 * Make a fetch request to the cache. Similar api to fetch(url, opts) which fetches from network.
 * Returns null if response not found
 *
 * @param url url to fetch
 * @param options fetch options
 * @param ttl time-to-live in seconds
 */
export async function fromFetchCache(url: string, options: RequestInit, ttl = DEFAULT_TTL): Promise<Response | null> {
  if (canUseFetchCache()) {
    const cacheRequest = _getCacheRequest(url, options);
    const fetchCache = await caches.open(CACHE_NAME);
    const cachedResponse = await fetchCache.match(cacheRequest);
    if (cachedResponse && !_isResponseStale(cachedResponse, ttl)) {
      return cachedResponse;
    }
  }

  return null;
}

/**
 * @param url url that was fetched
 * @param options options passed to fetch()
 * @param response response returned by fetch() that will be cached with url+opts as the key
 */
export async function updateFetchCache(url: string, options: RequestInit, response: Response): Promise<void> {
  if (canUseFetchCache()) {
    // we use response.clone because response body can only be read once
    // otherwise we get a 'body stream is locked' error
    const clonedResponse = response.clone();
    const cacheRequest = _getCacheRequest(url, options);
    const fetchCache = await caches.open(CACHE_NAME);

    if (cacheRequest.method === `GET` && clonedResponse.ok && clonedResponse.status === 200) {
      await fetchCache.put(cacheRequest, clonedResponse);
    }
  }
}

export async function deleteFromFetchCache(url: string, options: RequestInit): Promise<boolean> {
  if (canUseFetchCache()) {
    const cacheRequest = _getCacheRequest(url, options);
    const fetchCache = await caches.open(CACHE_NAME);
    return await fetchCache.delete(cacheRequest);
  }

  return false;
}

/**
 * Delete all cache entries older than DEFAULT_TTL
 * Also delete all caches other than CACHE_NAME in case version changed.
 *
 * @param opts
 * @param opts.everything - if true, delete all caches, regardless if responses are under ttl or not
 * @param opts.ttl - delete entries above the ttl in seconds
 */
export async function purgeFetchCache({everything = false, ttl = DEFAULT_TTL} = {}): Promise<void> {
  if (canUseFetchCache()) {
    const cacheNames = await caches.keys();
    for (const cacheName of cacheNames) {
      if (cacheName === CACHE_NAME && !everything) {
        const fetchCache = await caches.open(CACHE_NAME);
        const requests = await fetchCache.keys();
        for (const request of requests) {
          const response = await fetchCache.match(request);
          if (response && _isResponseStale(response, ttl)) {
            // delete individual entries that are stale
            await fetchCache.delete(request);
          }
        }
      } else {
        // delete entire named cache which nukes all entries contained within it
        await caches.delete(cacheName);
      }
    }
  }
}

/**
 * NOTE: This is only used by standalone mode for dev via fetch_cache url param.
 * DO NOT use this function directly in prod.
 * It is aggressive and caches all fetch GET requests which is undesirable
 * It also does not update the ui once the network version is ready, so user may see stale data.
 */
export function installDevFetchCache(hydrate = true) {
  if (!canUseFetchCache()) {
    console.warn(`caches api not available, fetch cache not installed`);
    return;
  }

  if (window[`__origFetch`]) {
    // already installed
    return;
  }

  // clear out cache entries above TTL to save space
  purgeFetchCache();

  // fetch from network in background and update cache when it returns
  async function fetchAndHydrate(url: string, options: RequestInit) {
    const networkResponse = await window[`__origFetch`](url, options);
    updateFetchCache(url, options, networkResponse);
    return networkResponse;
  }

  window[`__origFetch`] = window.fetch;
  window.fetch = async function fetch(url: string, options: RequestInit) {
    let cachedResponse = null;
    let networkPromise = null;

    if (hydrate) {
      networkPromise = fetchAndHydrate(url, options);
    }

    try {
      cachedResponse = await fromFetchCache(url, options);
    } catch {
      // FetchCacheMiss, fallback to network version
      return await (networkPromise || fetchAndHydrate(url, options));
    }

    // otherwise return cached version
    return cachedResponse;
  };
}
